import { useEffect, useState } from 'react';
import { ChartBar } from '@phosphor-icons/react';

import { HeaderContent, HEADER_TITLE_CLASS } from '../../components/shell/app-header';
import { api } from '../../lib/api';

interface Classe { id: string; name: string }

/* Forme exacte de HgDiagnosticClasseRow / HgDiagnosticNotionRow
   (packages/application/src/trpc/rows/hg-college.ts) — recopiée à la main,
   comme le reste des écrans HG collège : apps/web ne résout pas
   @naia-app/application au typecheck. */
export interface DiagnosticNotionRow {
  notion_code: string;
  notion_libelle: string | null;
  critere_libelle: string | null;
  effectif_concerne: number;
  notion_mobilisee: number;
  confusion_identifiee: number;
  absence_de_repere: number;
}

export interface DiagnosticClasseRow {
  classroom_id: string;
  effectif_classe: number;
  reponses_avec_notion: number;
  reponses_total: number;
  notions: DiagnosticNotionRow[];
}

const badge = (n: number, cls: string, label: string) => (
  <span className={`shrink-0 rounded-full px-[9px] py-[3px] text-[11px] font-extrabold uppercase tracking-[0.05em] ${cls}`}>
    {n} {label}
  </span>
);

export function NotionCard({ notion }: { notion: DiagnosticNotionRow }) {
  return (
    <li className="rounded-sm border border-border bg-card px-4 py-3 flex flex-col gap-2">
      <div>
        <p className="m-0 font-bold text-sm text-foreground">
          {notion.notion_libelle ?? `Notion « ${notion.notion_code} »`}
        </p>
        {notion.notion_libelle === null ? (
          <p className="m-0 mt-0.5 text-[12.5px] text-muted-foreground">
            Libellé non résolu — identifiée par son code uniquement.
          </p>
        ) : null}
        {notion.critere_libelle ? (
          <p className="m-0 mt-0.5 text-[12.5px] text-muted-foreground">{notion.critere_libelle}</p>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[12.5px] text-muted-foreground">
          {notion.effectif_concerne} élève{notion.effectif_concerne > 1 ? 's' : ''} concerné{notion.effectif_concerne > 1 ? 's' : ''} :
        </span>
        {notion.confusion_identifiee > 0 ? badge(notion.confusion_identifiee, 'bg-naia-orange-bg text-naia-orange', 'confusion identifiée') : null}
        {notion.absence_de_repere > 0 ? badge(notion.absence_de_repere, 'bg-naia-error-bg text-naia-error', 'absence de repère') : null}
        {notion.notion_mobilisee > 0 ? badge(notion.notion_mobilisee, 'bg-naia-green-bg text-naia-green-deep', 'notion mobilisée') : null}
      </div>
    </li>
  );
}

export default function HgCollegeEnseignant() {
  const [classes, setClasses] = useState<Classe[]>([]);
  const [classeId, setClasseId] = useState('');
  const [diagnostic, setDiagnostic] = useState<DiagnosticClasseRow | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);

  useEffect(() => {
    void api.teacher.myClassrooms.query()
      .then((rows) => {
        const cs = rows as Classe[];
        setClasses(cs);
        if (cs[0]) setClasseId(cs[0].id);
      })
      .catch(() => setLoadError('Impossible de charger vos classes.'));
  }, []);

  useEffect(() => {
    if (!classeId) return;
    let cancelled = false;
    setChargement(true);
    setLoadError(null);
    void api.miniApps.hgCollege.diagnostic.classe.query({ classroomId: classeId })
      .then((row) => { if (!cancelled) setDiagnostic(row as DiagnosticClasseRow); })
      .catch(() => { if (!cancelled) setLoadError('Impossible de charger le diagnostic de cette classe.'); })
      .finally(() => { if (!cancelled) setChargement(false); });
    return () => { cancelled = true; };
  }, [classeId]);

  const carte = 'bg-card border border-border rounded-card shadow-card p-5';
  const champ = 'h-11 min-h-[44px] w-full rounded-sm border border-border bg-card px-3 text-sm text-foreground';

  const couverturePct = diagnostic && diagnostic.reponses_total > 0
    ? Math.round((100 * diagnostic.reponses_avec_notion) / diagnostic.reponses_total)
    : null;

  return (
    <>
      <HeaderContent width="lg" showBurger backTo="/mini-apps" backLabel="Mini-apps">
        <div className="min-w-0 flex items-start gap-[10px]">
          <ChartBar size={24} weight="light" className="text-primary shrink-0 mt-[2px]" />
          <div className="min-w-0">
            <h1 className={HEADER_TITLE_CLASS}>Histoire-géographie — diagnostic de classe</h1>
            <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
              Les notions et confusions les plus fréquentes de la classe — jamais un classement d&apos;élèves.
            </p>
          </div>
        </div>
      </HeaderContent>

      <main className="max-w-[900px] mx-auto px-5 pb-16 pt-4 flex flex-col gap-5">
        {loadError ? (
          <p className="m-0 rounded-sm bg-naia-error-bg px-4 py-3 text-sm text-foreground">{loadError}</p>
        ) : null}

        <section className={carte}>
          <h2 className="text-[15px] font-extrabold text-foreground mt-0 mb-3">Classe</h2>
          {classes.length === 0 ? (
            <p className="m-0 text-sm text-muted-foreground">
              Aucune classe ne vous est rattachée. Votre direction peut vous en affecter une ;
              cet écran s&apos;ouvrira dès qu&apos;elle l&apos;aura fait.
            </p>
          ) : (
            <label className="text-[13px] font-bold text-muted-foreground block max-w-xs">
              Classe
              <select className={champ} value={classeId} onChange={(e) => setClasseId(e.target.value)}>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )}
        </section>

        {classeId ? (
          <section className={carte}>
            <h2 className="text-[15px] font-extrabold text-foreground mt-0 mb-1">Ce que la classe a traité</h2>

            {chargement ? (
              <p className="m-0 text-sm text-muted-foreground">Chargement…</p>
            ) : !diagnostic || diagnostic.reponses_total === 0 ? (
              <p className="m-0 text-sm text-muted-foreground">
                Aucune tentative enregistrée pour cette classe pour le moment.
              </p>
            ) : (
              <>
                <p className="m-0 mb-4 text-[13px] text-muted-foreground">
                  {couverturePct}&nbsp;% des réponses ({diagnostic.reponses_avec_notion} sur {diagnostic.reponses_total})
                  portent un diagnostic par notion. Le reste vient de fiches qui ne déclarent pas encore de
                  notion — ce n&apos;est pas une absence de confusion, c&apos;est un diagnostic non disponible,
                  et il n&apos;apparaît pas ci-dessous.
                </p>

                {diagnostic.notions.length === 0 ? (
                  <p className="m-0 text-sm text-muted-foreground">
                    Aucun diagnostic par notion disponible pour cette classe : les exercices faits jusqu&apos;ici
                    ne déclarent pas de notion identifiée.
                  </p>
                ) : (
                  <ul className="list-none m-0 p-0 flex flex-col gap-2">
                    {diagnostic.notions.map((notion) => (
                      <NotionCard key={notion.notion_code} notion={notion} />
                    ))}
                  </ul>
                )}
              </>
            )}
          </section>
        ) : null}
      </main>
    </>
  );
}
